import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { fnfAPI, payrollAPI, employeeAPI, salaryAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Plus, Loader2, ChevronRight, Users, Banknote, TrendingDown, Wallet, User, FileText, Download } from 'lucide-react';
import { formatIndianCurrency } from '../../lib/utils';
import ModuleDisabled from '../../components/shared/ModuleDisabled';
import { format } from 'date-fns';

const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' },
];

const STATUS_STYLES = {
  DRAFT: 'bg-gray-100 text-gray-700',
  PROCESSING: 'bg-yellow-100 text-yellow-800',
  PROCESSED: 'bg-blue-100 text-blue-800',
  LOCKED: 'bg-green-100 text-green-800',
};

const Payroll = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [formData, setFormData] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  });

  const [activeTab, setActiveTab] = useState('runs');
  const [fnfSettlements, setFnfSettlements] = useState([]);

  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState('');

  useEffect(() => { 
    if (activeTab === 'runs') fetchRuns(); 
    else fetchFnF();
  }, [activeTab]);

  useEffect(() => {
    if (isCreateOpen) {
      fetchActiveEmployees();
      setEmployeeSearch('');
    }
  }, [isCreateOpen]);

  const fetchActiveEmployees = async () => {
    try {
      const [empRes, salRes] = await Promise.all([
        employeeAPI.getEmployees({ status: 'ACTIVE', page_size: 1000 }),
        salaryAPI.getStructures({ is_active: true, page_size: 1000 })
      ]);
      
      const activeEmps = empRes.data.employees || [];
      const activeSalaries = salRes.data || [];
      const empIdsWithSalary = new Set(activeSalaries.map(s => s.employee_id));
      
      const filteredEmps = activeEmps.filter(e => empIdsWithSalary.has(e.id));
      
      setEmployees(filteredEmps);
      setSelectedEmployees(filteredEmps.map(e => e.id));
    } catch (err) {
      toast.error('Failed to fetch active employees or salary structures');
    }
  };

  const fetchRuns = async () => {
    try {
      const res = await payrollAPI.getRuns();
      setRuns(res.data);
    } catch (err) {
      if (err.response?.status === 403) setError('module_disabled');
      else toast.error('Failed to fetch payroll runs');
    } finally {
      setLoading(false);
    }
  };

  const fetchFnF = async () => {
    setLoading(true);
    try {
      const res = await fnfAPI.getAll();
      setFnfSettlements(res.data);
    } catch (err) {
      toast.error('Failed to fetch settlements');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (selectedEmployees.length === 0) {
      toast.error('Please select at least one employee for the payroll run');
      return;
    }
    setCreateLoading(true);
    try {
       const res = await payrollAPI.createRun({
        company_id: user.company_id,
        month: formData.month,
        year: formData.year,
        employee_ids: selectedEmployees,
      });
      toast.success('Payroll created');
      setIsCreateOpen(false);
      navigate(`/payroll/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create payroll');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDownloadFnF = async (id, empName) => {
    try {
      const response = await fnfAPI.downloadPdf(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `FnF_Statement_${empName.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  if (loading && runs.length === 0 && fnfSettlements.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-slate-500" />
      </div>
    );
  }

  if (error === 'module_disabled') return <ModuleDisabled moduleName="payroll" />;

  return (
    <div className="space-y-6 animate-slide-up">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Payroll & Settlements</h1>
          <p className="text-muted-foreground text-sm sm:text-base">Manage monthly runs and final settlements.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/fnf')}>
            <Plus className="mr-2 h-4 w-4" />
            New FnF
          </Button>
          <Button onClick={() => setIsCreateOpen(true)} className="bg-primary hover:bg-slate-800">
            <Plus className="mr-2 h-4 w-4" />
            Create Payroll
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b">
        <button
          onClick={() => setActiveTab('runs')}
          className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 ${
            activeTab === 'runs' 
              ? 'border-primary text-slate-900 bg-slate-50' 
              : 'border-transparent text-muted-foreground hover:text-slate-900'
          }`}
        >
          Monthly Payroll Runs
        </button>
        <button
          onClick={() => setActiveTab('fnf')}
          className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 ${
            activeTab === 'fnf' 
              ? 'border-primary text-slate-900 bg-slate-50' 
              : 'border-transparent text-muted-foreground hover:text-slate-900'
          }`}
        >
          Exits & Settlements (FnF)
        </button>
      </div>

      {/* Content */}
      <div className="space-y-4">
        {activeTab === 'runs' ? (
          runs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-muted-foreground gap-3">
              <Wallet className="h-12 w-12 opacity-20" />
              <p>No payroll runs yet. Create one to get started.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {runs.map((run) => {
                const monthLabel = MONTHS.find((m) => m.value === run.month)?.label;
                return (
                  <div
                    key={run.id}
                    onClick={() => navigate(`/payroll/${run.id}`)}
                    className="border rounded-xl bg-white p-4 cursor-pointer hover:shadow-md hover:border-slate-300 transition-all flex items-center gap-4"
                  >
                    <div className="w-14 h-14 rounded-xl bg-slate-50 border border-slate-100 flex flex-col items-center justify-center shrink-0">
                      <span className="text-xs font-medium text-slate-500 leading-none">
                        {monthLabel?.slice(0, 3).toUpperCase()}
                      </span>
                      <span className="text-sm font-bold text-slate-900 leading-tight">{run.year}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-base">{monthLabel} {run.year} Payroll</span>
                        <Badge className={STATUS_STYLES[run.status]}>{run.status}</Badge>
                        {run.pay_period_start && (
                          <span className="text-[11px] font-medium text-blue-600 bg-blue-50/50 px-2 py-0.5 rounded-md border border-blue-100/50">
                            For {MONTHS.find(m => m.value === (new Date(run.pay_period_start).getMonth() + 1))?.label} {new Date(run.pay_period_start).getFullYear()}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground flex-wrap">
                        <span className="flex items-center gap-1">
                          <Users className="h-3.5 w-3.5" />
                          {run.employee_count} employees
                        </span>
                        <span className="flex items-center gap-1">
                          <Banknote className="h-3.5 w-3.5" />
                          Net: {formatIndianCurrency(run.total_net)}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
                  </div>
                );
              })}
            </div>
          )
        ) : (
          /* FnF Settlements List */
          fnfSettlements.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-muted-foreground gap-3">
              <FileText className="h-12 w-12 opacity-20" />
              <p>No settlements processed yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {fnfSettlements.map((fnf) => (
                <div
                  key={fnf.id}
                  className="border rounded-xl bg-white p-4 hover:shadow-md transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                      <User className="h-6 w-6 text-slate-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">{fnf.employee_name}</h4>
                      <p className="text-xs text-muted-foreground">
                        {fnf.employee_code} • Last Working Day: {format(new Date(fnf.exit_date), 'dd MMM yyyy')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between sm:justify-end gap-6 w-full sm:w-auto">
                    <div className="text-right">
                      <p className="text-sm font-bold text-slate-900">{formatIndianCurrency(fnf.net_payable)}</p>
                      <Badge className="bg-green-50 text-green-700 border-green-100 font-normal">Finalized</Badge>
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="border-slate-200 text-slate-700 hover:bg-slate-50"
                      onClick={() => handleDownloadFnF(fnf.employee_id, fnf.employee_name)}
                    >
                      <Download className="h-4 w-4 mr-2" /> Statement
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Create Payroll</DialogTitle>
            <DialogDescription>Select a month, year, and the employees to include in this payroll run.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate}>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label>Month</Label>
                <Select
                  value={String(formData.month)}
                  onValueChange={(v) => setFormData({ ...formData, month: Number(v) })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m) => (
                      <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Year</Label>
                <Input
                  type="number"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: Number(e.target.value) })}
                  min={2020} max={2035}
                />
              </div>
            </div>

            {/* Employee Selector */}
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold text-slate-800">Select Employees for Payroll</Label>
                <span className="text-xs text-muted-foreground font-medium">
                  {selectedEmployees.length} of {employees.length} selected
                </span>
              </div>
              
              <div className="flex gap-2">
                <Input
                  placeholder="Search employees..."
                  value={employeeSearch}
                  onChange={(e) => setEmployeeSearch(e.target.value)}
                  className="h-9 text-sm"
                  type="text"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (selectedEmployees.length === employees.length) {
                      setSelectedEmployees([]);
                    } else {
                      setSelectedEmployees(employees.map(e => e.id));
                    }
                  }}
                  className="shrink-0 h-9 text-xs"
                >
                  {selectedEmployees.length === employees.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>

              <div className="border rounded-lg max-h-48 overflow-y-auto divide-y bg-slate-50/50">
                {employees
                  .filter(e => {
                    const fullName = `${e.first_name || ''} ${e.last_name || ''}`.toLowerCase();
                    const code = (e.employee_code || '').toLowerCase();
                    const searchLower = employeeSearch.toLowerCase();
                    return fullName.includes(searchLower) || code.includes(searchLower);
                  })
                  .map((emp) => {
                    const isChecked = selectedEmployees.includes(emp.id);
                    return (
                      <div
                        key={emp.id}
                        onClick={() => {
                          if (isChecked) {
                            setSelectedEmployees(selectedEmployees.filter(id => id !== emp.id));
                          } else {
                            setSelectedEmployees([...selectedEmployees, emp.id]);
                          }
                        }}
                        className="flex items-center gap-3 px-3 py-2 hover:bg-white cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {}} // handled by click on parent div
                          className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary shrink-0 cursor-pointer"
                        />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-800 leading-tight">
                            {emp.first_name} {emp.last_name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {emp.employee_code} {emp.department && `• ${emp.department}`}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                {employees.length === 0 && (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    No active employees found.
                  </div>
                )}
              </div>
            </div>

            <DialogFooter className="mt-6 border-t pt-4">
              <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={createLoading}>
                {createLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Payroll;
